import React, { useEffect, useRef, useState } from "react";
import { Animated, Easing, StyleSheet, View, Platform } from "react-native";
import { theme } from "../theme/theme";
import QuickAccessPanel from "./QuickAccessPanel";
import useNavAutoHide from "../hooks/useNavAutoHide";
import { useAccessibility } from "../hooks/useAccessibility";

/**
 * The quick access menu, with the phone-sized behaviour of hiding while a
 * member is working. See useNavAutoHide for when it hides and why.
 *
 * The panel itself is left alone. It keeps owning its row, its drift and its
 * badges; this only clips it and slides it, so the two concerns stay separable
 * and the panel's own tests keep testing the panel.
 *
 * How the tuck is built: the outer view clips from full height to zero while
 * the inner one keeps its full height. Animating the panel's own height instead
 * would re-lay-out the row on every frame, and the row measures itself to
 * decide its icon spacing — so it would recompute the spacing mid animation
 * and the icons would visibly shuffle.
 */

/** Long enough to read as the row moving, short enough not to be in the way. */
const TUCK_DURATION = 260;

const PersistentQuickAccess = ({activeTab, onLayout, ...panelProps}) => {
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    document.documentElement.dataset.oeNavOpen = "true";
    document.documentElement.dataset.oeNavPinned = "true";
    window.dispatchEvent(new CustomEvent("oe-nav-visibility", {detail: {open: true, pinned: true}}));
    return () => {
      delete document.documentElement.dataset.oeNavPinned;
      delete document.documentElement.dataset.oeNavOpen;
      window.dispatchEvent(new CustomEvent("oe-nav-visibility", {detail: {open: false, pinned: false}}));
    };
  }, []);
  return (
    <View nativeID="oe-quick-access-panel" onLayout={onLayout} style={[styles.clip, styles.pinned]}>
      <QuickAccessPanel {...panelProps} activeTab={activeTab} playHint={false} />
    </View>
  );
};

const AutoHideQuickAccess = ({ activeTab, onLayout, ...panelProps }) => {
  const { collapsed, playHint, onHintComplete } = useNavAutoHide({
    activeTab,
    // The walkthrough points callouts at these icons from a hardcoded panel
    // height (see QUICK_ACCESS_PANEL_HEIGHT in TutorialScreenMinimal), so a row
    // that tucked itself away mid-tutorial would leave arrows aimed at nothing.
    enabled: activeTab !== "tutorial-minimal",
  });
  const { reduceMotion } = useAccessibility();
  useEffect(() => {
    if (typeof window === "undefined") return;
    document.documentElement.dataset.oeNavOpen = String(!collapsed);
    document.documentElement.dataset.oeNavPinned = "false";
    window.dispatchEvent(new CustomEvent("oe-nav-visibility", { detail: { open: !collapsed, pinned: false } }));
  }, [collapsed]);


  const [panelHeight, setPanelHeight] = useState(0);

  // Seeded from the first value of `collapsed`, not from 0. A member returning
  // to the app should find the row already tucked; starting at 0 would play a
  // slide-up on arrival, which is the "every load nags you" behaviour again.
  const progress = useRef(new Animated.Value(collapsed ? 1 : 0)).current;

  useEffect(() => {
    const toValue = collapsed ? 1 : 0;

    if (reduceMotion) {
      progress.setValue(toValue);
      return undefined;
    }

    const animation = Animated.timing(progress, {
      toValue,
      duration: TUCK_DURATION,
      easing: Easing.inOut(Easing.ease),
      // Height is a layout property, so this cannot run on the native driver.
      useNativeDriver: false,
    });

    animation.start();
    return () => animation.stop();
  }, [collapsed, reduceMotion, progress]);

  const canTuck = panelHeight > 0;

  const height = canTuck
    ? progress.interpolate({
        inputRange: [0, 1],
        outputRange: [panelHeight, 0],
      })
    : // Not measured yet. A member who arrives already tucked gets height 0,
      // because falling back to the natural height here would flash the whole
      // row for a frame on every single load.
      (collapsed ? 0 : undefined);

  return (
    <Animated.View
      nativeID="oe-quick-access-panel"
      onLayout={onLayout}
      style={[styles.clip, height !== undefined && { height }]}
    >
      <Animated.View
        onLayout={(event) => setPanelHeight(event.nativeEvent.layout.height)}
        pointerEvents={collapsed ? "none" : "auto"}
      >
        <QuickAccessPanel
          activeTab={activeTab}
          playHint={playHint}
          onHintComplete={onHintComplete}
          {...panelProps}
        />
      </Animated.View>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  pinned: {position: Platform.OS === "web" ? "fixed" : "absolute", bottom: 0, left: 0, right: 0, zIndex: 1000, flexShrink: 0, borderTopWidth: 1, borderTopColor: theme.colors.navigationOutline, backgroundColor: theme.colors.navigationSurface, paddingBottom: Platform.OS === "web" ? "env(safe-area-inset-bottom, 0px)" : 0},
  clip: {
    width: "100%",
    overflow: "hidden",
    // Keeps the row above the page while it slides, rather than the page content
    // riding over the top of it mid animation.
    zIndex: 5,
  },
});

export default function CollapsibleQuickAccess({persistent = false, ...props}) {
  return persistent ? <PersistentQuickAccess {...props} /> : <AutoHideQuickAccess {...props} />;
}
